import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { ReasonDialog } from "../operations/OperationDocumentDialogs";
import type { AppRole } from "../types";
import {
  RECEIVABLE_TRANSITIONS,
  canRunTransition,
  findTransition,
} from "./invoicingRules";
import type { ReceivableSummary } from "./invoicingTypes";
import { useInvoicingRun } from "./useInvoicingRun";

const InvoiceFolioDialog = ({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (folio: string) => Promise<string | null>;
}) => {
  const translate = useTranslate();
  const [folio, setFolio] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    const message = await onSubmit(folio.trim());
    setBusy(false);
    if (message) setError(message);
  };

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("crm.invoicing.receivables.invoice_title")}
          </DialogTitle>
          <DialogDescription>
            {translate("crm.invoicing.receivables.invoice_description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="invoice-folio">
            {translate("crm.invoicing.receivables.invoice_folio")}
          </Label>
          <Input
            id="invoice-folio"
            className="h-11 sm:h-9"
            value={folio}
            onChange={(event) => setFolio(event.target.value)}
          />
        </div>
        {error ? (
          <p role="alert" className="text-sm text-[#B3321C]">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={onClose}
          >
            {translate("crm.operations.documents.cancel")}
          </Button>
          <Button
            type="button"
            variant="gradient"
            className="h-11 sm:h-9"
            disabled={busy || folio.trim() === ""}
            onClick={confirm}
          >
            {translate("crm.operations.documents.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * The actions of a receivable, each shown only to the roles the E6 transition
 * table allows (the RPCs enforce the same table): Facturada (folio required)
 * and Cobrada for contabilidad / direccion, Rechazar back to the previous
 * step (reason required). `pendiente_vobo` is resolved on "Pendientes de
 * VoBo" and auditoria never sees an action.
 */
export const ReceivableActions = ({
  receivable,
  role,
}: {
  receivable: ReceivableSummary;
  role: AppRole;
}) => {
  const translate = useTranslate();
  const run = useInvoicingRun();
  const [dialog, setDialog] = useState<"invoice" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [collecting, setCollecting] = useState(false);
  const close = () => setDialog(null);
  const collect = async () => {
    setCollecting(true);
    setError(null);
    const message = await run(
      (provider) => provider.markReceivableCollected(receivable.id),
      "crm.invoicing.receivables.done.collected",
    );
    setCollecting(false);
    setError(message);
  };
  const can = (action: string) =>
    canRunTransition(RECEIVABLE_TRANSITIONS, action, receivable.status, role);
  const rejectTarget = findTransition(
    RECEIVABLE_TRANSITIONS,
    "reject",
    receivable.status,
  )?.to;
  const canReject = receivable.status !== "pendiente_vobo" && can("reject");
  // QE6-A6: no Rechazar once collected; later corrections are by adjustment.
  if (
    receivable.status === "cobrada" &&
    (role === "direccion" || role === "contabilidad")
  ) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="final-note">
        {translate("crm.invoicing.receivables.final_note")}
      </p>
    );
  }
  if (!can("invoice") && !can("collect") && !canReject) return null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {can("invoice") ? (
          <Button
            type="button"
            variant="gradient"
            className="h-11 sm:h-9"
            onClick={() => setDialog("invoice")}
          >
            {translate("crm.invoicing.receivables.mark_invoiced")}
          </Button>
        ) : null}
        {can("collect") ? (
          <Button
            type="button"
            variant="gradient"
            className="h-11 sm:h-9"
            disabled={collecting}
            aria-busy={collecting}
            onClick={collect}
          >
            {collecting ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : null}
            {translate("crm.invoicing.receivables.mark_collected")}
          </Button>
        ) : null}
        {canReject && rejectTarget ? (
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={() => setDialog("reject")}
          >
            {translate("crm.invoicing.receivables.reject")}
          </Button>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-[#B3321C]">
          {error}
        </p>
      ) : null}
      {dialog === "invoice" ? (
        <InvoiceFolioDialog
          onClose={close}
          onSubmit={async (folio) => {
            const message = await run(
              (provider) =>
                provider.markReceivableInvoiced(receivable.id, folio),
              "crm.invoicing.receivables.done.invoiced",
            );
            if (!message) close();
            return message;
          }}
        />
      ) : null}
      {dialog === "reject" && rejectTarget ? (
        <ReasonDialog
          title={translate("crm.invoicing.receivables.reject_title")}
          description={translate("crm.invoicing.receivables.reject_to", {
            status: translate(
              `crm.invoicing.status.receivable.${rejectTarget}`,
            ),
          })}
          onClose={close}
          onSubmit={async (reason) => {
            const message = await run(
              (provider) => provider.rejectReceivable(receivable.id, reason),
              "crm.invoicing.receivables.done.rejected",
            );
            if (!message) close();
            return message;
          }}
        />
      ) : null}
    </div>
  );
};
